import { Spacing } from '@persona-id/design-tokens';
import { useCallback, useEffect, useState } from 'react';
import { DropzoneProps, useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { InjectedFormProps, reduxForm } from 'redux-form';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import Form from 'dashboard/layouts/FormLayout/Form';
import { User, UserResponseAttributes } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import IconUserProfilePicture from 'images/icons/user-profile-picture.svg';

const ACCEPTED_PROFILE_PICTURE_FORMATS = ['image/jpeg', 'image/png'];
const FORM_NAME = 'formCurrentUserProfilePicture';
const PROFILE_PICTURE_WIDTH = 160;
const PROFILE_PICTURE_HEIGHT = 160;

const StyledForm = styled(Form)`
  width: min-content;
`;

const StyledButton = styled(Button)`
  margin-top: ${Spacing[2]};
  width: ${PROFILE_PICTURE_WIDTH}px;
`;

const ProfilePictureImage = styled('img')`
  border-radius: ${BORDER_RADIUSES.rounded};
  object-fit: cover;
`;

const StyledIconUserProfilePicture = styled(IconUserProfilePicture)`
  border-radius: ${BORDER_RADIUSES.rounded};
`;

interface FormCurrentUserProfilePictureProps {
  currentUser: User;
}

const FormCurrentUserProfilePicture = ({
  currentUser,
  handleSubmit,
  change,
  dirty,
  submitting,
  pristine,
}: InjectedFormProps &
  FormCurrentUserProfilePictureProps &
  Pick<DropzoneProps, 'accept' | 'minSize' | 'maxSize' | 'maxFiles' | 'disabled'>) => {
  const profilePictureUrl = useSelector((state: RootState) =>
    dirty
      ? state.form[FORM_NAME]?.values?.profilePicture?.preview
      : (currentUser.attributes as UserResponseAttributes)['profile-picture-url'],
  );

  const [madeChangesAfterSave, setMadeChangesAfterSave] = useState(false);

  useEffect(() => {
    setMadeChangesAfterSave(false);
  }, [(currentUser.attributes as UserResponseAttributes)['profile-picture-url']]);

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        change('profilePicture', null);
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        acceptedFiles.forEach((file: FileWithPreview) => {
          file.isImage = file.type.includes('image');
          const reader = new FileReader();
          reader.onload = function (_) {
            file.preview = file.isImage
              ? URL.createObjectURL(file)
              : `${file.name} (${file.size} bytes)`;
            change('profilePicture', file);
          };
          reader.readAsText(file);
        });
      }
      setMadeChangesAfterSave(true);
    },
    [change],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    accept: ACCEPTED_PROFILE_PICTURE_FORMATS,
    multiple: false,
  });

  return (
    <StyledForm onSubmit={handleSubmit}>
      {profilePictureUrl ? (
        <ProfilePictureImage
          height={PROFILE_PICTURE_HEIGHT}
          width={PROFILE_PICTURE_WIDTH}
          src={profilePictureUrl}
        />
      ) : (
        <StyledIconUserProfilePicture
          height={PROFILE_PICTURE_HEIGHT}
          width={PROFILE_PICTURE_WIDTH}
        />
      )}
      <StyledButton
        {...getRootProps()}
        id='file-upload-container'
        aria-label='Upload file'
        disabled={submitting}
      >
        Upload photo
        <input {...getInputProps()} />
      </StyledButton>
      {profilePictureUrl && (
        <StyledButton
          data-test='form-current-user-profile-picture__remove-photo'
          onClick={() => {
            change('profilePicture', null);
            if ((currentUser.attributes as UserResponseAttributes)['profile-picture-url'] == null) {
              setMadeChangesAfterSave(false);
            } else {
              setMadeChangesAfterSave(true);
            }
          }}
          disabled={submitting}
        >
          Remove photo
        </StyledButton>
      )}
      <StyledButton
        type='submit'
        data-test='form-current-user-profile-picture__save'
        disabled={submitting || pristine || !madeChangesAfterSave}
        isPending={submitting}
      >
        Save changes
      </StyledButton>
    </StyledForm>
  );
};

export interface FormCurrentUserProfilePictureData {
  profilePicture: File;
}

export default reduxForm<FormCurrentUserProfilePictureData, FormCurrentUserProfilePictureProps>({
  form: FORM_NAME,
  enableReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormCurrentUserProfilePicture);
